import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { leagueMeta, leagueOrder, matches, setPageMeta, siteInfo, statusMeta } from '@/lib/site';

const values = [
  {
    icon: 'ri-compass-3-line',
    title: '集中入口，一处看全',
    desc: '把分散在各平台的赛事时间、对阵与直播状态汇总到同一页面，减少来回查找。',
  },
  {
    icon: 'ri-filter-3-line',
    title: '筛选与搜索并重',
    desc: '既可按联赛、状态、时间层层筛选，也能直接用球队名搜索，两种路径互补。',
  },
  {
    icon: 'ri-line-chart-line',
    title: '赛况演示清晰',
    desc: '详情页用比分、分节得分与命中率对比呈现比赛走势，帮助快速理解比赛进程。',
  },
];

const scopeItems = [
  'NBA、CBA 与 FIBA 国际赛事的赛程与直播状态展示',
  '按直播状态、时间范围筛选与关键词搜索',
  '单场赛事的阵容、赛况演示与相关推荐',
  '移动端适配，支持手机浏览与滑动手势',
];

export default function AboutPage() {
  useEffect(() => {
    setPageMeta(
      '关于我们 - 篮球直播',
      '了解篮球直播的网站定位与服务内容：专注 NBA、CBA 与国际篮球赛事直播信息聚合，提供赛程、直播状态与赛况演示。',
      '篮球直播,关于我们,篮球赛事平台,NBA直播,CBA直播',
    );
  }, []);

  const total = matches.length;
  const live = matches.filter((m) => m.status === 'live').length;

  return (
    <div className="w-full">
      <section className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-6 lg:px-8 py-9 md:py-12">
          <div className="text-background-300 [&_a:hover]:text-accent-300">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          </div>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-8">
              <p className="font-heading text-[11px] tracking-[0.3em] text-accent-400 mb-2">ABOUT US</p>
              <h1 className="font-heading text-2xl md:text-4xl text-background-50 leading-tight">
                关于 {siteInfo.name}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300">
                {siteInfo.name}是一个专注篮球赛事的直播信息平台。我们把 NBA、CBA 与国际篮球赛事的开赛时间、对阵信息与直播状态整理成清晰的时间线，帮助球迷在最短时间内找到今晚想看的那一场比赛。
              </p>
            </div>
            <div className="lg:col-span-4 flex items-center gap-6 lg:justify-end">
              <div>
                <p className="font-heading text-3xl text-accent-400 leading-none">{total}</p>
                <p className="mt-1.5 text-[11px] text-background-400">在架赛事</p>
              </div>
              <span className="w-px h-9 bg-primary-800" />
              <div>
                <p className="font-heading text-3xl text-background-50 leading-none">{live}</p>
                <p className="mt-1.5 text-[11px] text-background-400">直播中</p>
              </div>
              <span className="w-px h-9 bg-primary-800" />
              <div>
                <p className="font-heading text-3xl text-background-50 leading-none">3</p>
                <p className="mt-1.5 text-[11px] text-background-400">联赛分类</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5">
          <div className="lg:col-span-8 space-y-4 md:space-y-5">
            <section aria-labelledby="position" className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
              <h2 id="position" className="font-heading text-lg text-foreground-950 flex items-center gap-2">
                <i className="ri-focus-3-line text-primary-600" />
                网站定位
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                面向篮球爱好者、体育迷与关注 NBA、CBA 及国际篮球比赛的观众，{siteInfo.name}提供赛事的实时直播状态与相关信息。用户无需在多个页面之间反复跳转，即可通过列表、筛选与搜索快速定位目标赛事，并在详情页查看双方阵容、赛况与直播入口。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                我们的目标很简单：让你打开页面后，三十秒内知道今天有哪些比赛值得看、什么时候开始、能不能马上观看。
              </p>
            </section>

            <section aria-labelledby="service" className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
              <h2 id="service" className="font-heading text-lg text-foreground-950 flex items-center gap-2">
                <i className="ri-stack-line text-primary-600" />
                服务内容
              </h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {values.map((item) => (
                  <div key={item.title}>
                    <span className="w-10 h-10 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center text-xl mb-2.5">
                      <i className={item.icon} />
                    </span>
                    <h3 className="text-sm font-medium text-foreground-900">{item.title}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section aria-labelledby="scope" className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
              <h2 id="scope" className="font-heading text-lg text-foreground-950 flex items-center gap-2">
                <i className="ri-list-check-2 text-primary-600" />
                覆盖范围
              </h2>
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {scopeItems.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-check-line text-accent-600 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {leagueOrder.map((key) => (
                  <Link
                    key={key}
                    to={`/category/${key}`}
                    className="flex items-center justify-between gap-2 rounded-md bg-background-100 border border-background-200 px-3 py-2.5 text-xs text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <i className={leagueMeta[key].icon} />
                      {leagueMeta[key].name}
                    </span>
                    <i className="ri-arrow-right-s-line" />
                  </Link>
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:col-span-4 space-y-4">
            <div className="rounded-lg bg-background-50 border border-background-200 p-5">
              <h3 className="font-heading text-sm tracking-wider text-foreground-900">联系方式</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                合作、内容纠错或赛事信息反馈，欢迎通过邮件与我们联系。
              </p>
              <a
                href={`mailto:${siteInfo.email}`}
                className="mt-3 flex items-center gap-2 rounded-md bg-primary-50 px-3 py-2.5 text-sm text-primary-800 hover:bg-primary-100 transition-colors"
              >
                <i className="ri-mail-line" />
                <span className="truncate">{siteInfo.email}</span>
              </a>
              <p className="mt-2.5 text-[11px] text-foreground-500">我们通常在 1-2 个工作日内回复。</p>
            </div>

            <div className="rounded-lg bg-accent-50 border border-accent-200 p-5">
              <h3 className="font-heading text-sm tracking-wider text-accent-900 flex items-center gap-2">
                <i className="ri-error-warning-line" />
                使用说明
              </h3>
              <ul className="mt-2.5 space-y-2 text-xs leading-relaxed text-accent-900/80">
                <li>本站为前端演示站点，赛事时间、比分与赛况数据均为示例内容。</li>
                <li>“观看直播”按钮尚未接入真实流媒体服务，点击后仅展示说明提示。</li>
                <li>比赛画面与数据请以官方转播平台与官方数据为准。</li>
              </ul>
            </div>

            <div className="rounded-lg bg-background-50 border border-background-200 p-5">
              <h3 className="font-heading text-sm tracking-wider text-foreground-900">赛事状态说明</h3>
              <ul className="mt-2.5 space-y-2 text-xs">
                {(['live', 'upcoming', 'finished'] as const).map((key) => (
                  <li key={key} className="flex items-center gap-2 text-foreground-700">
                    <span className={`w-1.5 h-1.5 rounded-full ${statusMeta[key].dot}`} />
                    <span className="font-medium">{statusMeta[key].label}</span>
                    <span className="text-foreground-500">
                      {key === 'live' ? '比赛正在进行' : key === 'upcoming' ? '等待开赛' : '比赛已结束'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-primary-900 p-5">
              <h3 className="font-heading text-sm text-background-50">版权声明</h3>
              <p className="mt-2 text-[11px] leading-relaxed text-background-300">
                © 2024 {siteInfo.name}。站点内容（除赛事名称与球队名称外）为本站整理与展示，未经许可不得用于商业转载。赛事相关商标与版权归各联赛及球队所有。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}